import { useState } from 'react'
import AuthCode from 'react-auth-code-input'
import * as yup from 'yup'
import { ValidationError } from 'yup'
import { styled, theme } from '@/src/stitches.config'
import Button from '@/src/common/components/marketing/buttons/Button'
import { FormErrorDisplay } from '../../build-a-card/FormErrorDisplay'
import DefaultModal from '@/src/common/components/marketing/modals/DefaultModal'
import { formatPhoneNumber } from '@/src/common/utilities/phoneNumbers'
import { SuccessCheck } from '@/src/common/components/elements/Animations/SuccessCheck'
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons/faCircleCheck'
import { GradientText } from '../../pg-plus/PGPlusStyledComponents'
import { clearFormErrorForField } from '@/src/common/utilities/clearFormErrorForField'
import AlertWithIcon from '@/src/common/components/elements/Alerts/AlertWithIcon'
import handleEnterPressEvent from '@/src/common/utilities/handleEnterPressEvent'

export const verificationCodeSchema = yup.object({
    verificationCode: yup
        .string()
        .trim()
        .required()
        .label('Security code')
        .min(6, `Security code must be a 6-digit number`),
})

const Wrapper = styled('div', {
    width: '100%',
    maxWidth: 470,
    display: 'flex',
    fd: 'column',
    ai: 'center',
    zIndex: 2,
})

type TPhoneVerificationForm = {
    phone: string
    submitPhoneVerificationCode: Function
    resendPhoneVerificationCode: Function
    setShowModal: Function
    showModal: boolean
    success: boolean
    resendSuccess: boolean
    verificationError: boolean
}

export default function PhoneVerificationForm({
    phone,
    submitPhoneVerificationCode,
    resendPhoneVerificationCode,
    setShowModal,
    showModal = false,
    success = false,
    resendSuccess = false,
    verificationError = false,
}: TPhoneVerificationForm) {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [verificationCode, setVerificationCode] = useState<string>('')
    const [submissionErrors, setSubmissionErrors] = useState<ValidationError[]>(
        []
    )
    const verifyCode = async () => {
        setIsLoading(true)

        await verificationCodeSchema
            .validate({ verificationCode }, { abortEarly: false })
            .then(() => {
                submitPhoneVerificationCode(verificationCode)
            })
            .catch((err) => {
                setSubmissionErrors(err.inner)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    return (
        <DefaultModal
            showModal={showModal}
            setShowModal={setShowModal}
            testId={'phone-verification-modal'}
        >
            <Wrapper>
                {success ? (
                    <div>
                        <SuccessCheck
                            icon={faCircleCheck}
                            style={{ display: 'block' }}
                            fontSize={'5rem'}
                            color={`${theme.colors.success}`}
                        />
                        <GradientText
                            size={'sm'}
                            as={'p'}
                            style={{
                                fontSize: '1.5rem',
                                margin: '10px 0px 30px',
                            }}
                            data-testid="sms-success-message"
                        >
                            Thank you
                        </GradientText>
                    </div>
                ) : (
                    <>
                        <h2 style={{ textAlign: 'center' }}>
                            We care about your security
                        </h2>
                        <p style={{ textAlign: 'center', marginTop: 0 }}>
                            Please enter the security code sent to{' '}
                            <span
                                style={{
                                    fontWeight: 'bold',
                                    whiteSpace: 'pre',
                                }}
                            >
                                {formatPhoneNumber(phone)}
                            </span>
                        </p>

                        <AuthCode
                            length={6}
                            onChange={(val: string) => {
                                setVerificationCode(val.trim())
                                clearFormErrorForField({
                                    path: 'verificationCode',
                                    formErrors: submissionErrors,
                                    setFormErrors: setSubmissionErrors,
                                })
                            }}
                            containerClassName="authcode-container"
                            inputClassName="authcode-input"
                            allowedCharacters={'numeric'}
                            disabled={isLoading}
                        />
                        <FormErrorDisplay
                            formErrors={submissionErrors}
                            path={'verificationCode'}
                            style={{ marginBottom: 0, marginTop: 0 }}
                        />
                        <Button
                            label={isLoading ? 'Verifying...' : 'Continue'}
                            onClick={() => {
                                verifyCode()
                            }}
                            type={'button'}
                            width={'full'}
                            showArrow={true}
                            textTransform="uppercase"
                            isLoading={isLoading}
                            disabled={isLoading}
                            css={{ margin: '20px 0px' }}
                        />
                        <p>
                            Didn&apos;t get the code?{' '}
                            <span
                                style={{
                                    color: `${theme.colors.secondary}`,
                                    cursor: 'pointer',
                                }}
                                onClick={(e) => {
                                    resendPhoneVerificationCode()
                                }}
                                onKeyDown={(e) =>
                                    handleEnterPressEvent(e, () =>
                                        resendPhoneVerificationCode()
                                    )
                                }
                                role="button"
                            >
                                Resend
                            </span>
                        </p>
                        {verificationError ? (
                            <AlertWithIcon iconName={'error'} size={'sm'}>
                                <p data-testid="sms-error">
                                    Invalid security code. Please try again.
                                </p>
                            </AlertWithIcon>
                        ) : null}
                        {resendSuccess ? (
                            <AlertWithIcon iconName={'success'} size={'sm'}>
                                <p data-testid="sms-resend-success">
                                    Resent successfully
                                </p>
                            </AlertWithIcon>
                        ) : null}
                    </>
                )}
            </Wrapper>
        </DefaultModal>
    )
}
